import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, BACKEND_URL } from "@/lib/api";
import { Camera, Loader2, X } from "lucide-react";

const compress = (file, max = 1280, q = 0.8) => new Promise((resolve) => {
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, max / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = Math.round(img.width * scale); c.height = Math.round(img.height * scale);
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    c.toBlob((b) => resolve(b || file), "image/jpeg", q);
  };
  img.onerror = () => resolve(file);
  img.src = URL.createObjectURL(file);
});

export const fileUrl = (url) => (url?.startsWith("/api/") ? `${BACKEND_URL}${url}` : url);

export function PhotoUpload({ value, onChange, testId = "photo-upload", video = true }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true); setProgress(5);
    try {
      const isVideo = f.type.startsWith("video/");
      if (isVideo && f.size > 60 * 1024 * 1024) throw new Error("Video maksimal 60 MB");
      const blob = isVideo ? f : await compress(f);
      setProgress(20);
      const fd = new FormData();
      fd.append("file", blob, isVideo ? (f.name || "video.mp4") : "foto.jpg");
      const { data } = await api.post("/uploads", fd, { onUploadProgress: (p) => setProgress(20 + Math.round((p.loaded / (p.total || 1)) * 75)) });
      onChange(data.url);
      toast.success(isVideo ? "Video terunggah" : "Foto terunggah");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); setProgress(0); if (ref.current) ref.current.value = ""; }
  };
  return (
    <div data-testid={testId}>
      <input ref={ref} type="file" accept={video ? "image/*,video/*" : "image/*"} capture="environment" className="hidden" onChange={pick} data-testid={`${testId}-input`} />
      {value ? (
        <div className="relative w-fit">{/\.(mp4|mov|webm|m4v|3gp)(\?|$)/i.test(value) ? <video src={fileUrl(value)} controls className="h-32 rounded border border-zinc-800" data-testid={`${testId}-preview`} /> : <img src={fileUrl(value)} alt="foto" className="h-32 rounded border border-zinc-800 object-cover" data-testid={`${testId}-preview`} />}<button type="button" onClick={() => onChange("")} className="absolute -top-2 -right-2 bg-zinc-900 border border-zinc-700 rounded-full p-1" data-testid={`${testId}-remove`}><X className="h-3 w-3" /></button></div>
      ) : (
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-btn`} className="w-full border border-dashed border-zinc-700 hover:border-red-700 rounded-md py-4 flex flex-col items-center gap-1 text-sm text-zinc-400 transition-colors">
          {busy ? <><Loader2 className="h-5 w-5 animate-spin text-red-400" /><span>Mengunggah {progress}%</span><div className="w-40 h-1 bg-zinc-800 rounded mt-1"><div className="h-full bg-red-500 rounded" style={{ width: `${progress}%` }} /></div></> : <><Camera className="h-5 w-5 text-red-400" /><span>Ambil / pilih foto</span></>}
        </button>
      )}
    </div>
  );
}
